// Document preview (app.md §8). The pane asks the API for a short-lived signed
// URL for the upload's file and renders it with PDF.js onto canvases — one
// canvas per page in a scrollable pane, scrolled to the invoice's first page
// when its page span is known.
//
// The URL endpoint checks the caller's own document access before issuing
// anything. Since 2026-08-10 (owner ruling R2) a company approver at any of the
// three tiers DOES get a URL — for a packet holding one of their own invoices;
// the `uploads` row policy is what bounds it, not this pane. One upload can hold
// invoices for several companies and the pipeline cannot split it, so that
// packet is visible whole, and the exposure is accepted and recorded
// (security.md → PDF policy, docs/decisions.md).
// The pane is only rendered where canPreviewPdf(session) allows it, but
// that is a UI hint — the API is the authority.
//
// Rendering stays PDF.js rather than an iframe or the browser's plugin viewer:
// it draws pixels into canvases we control, which is what makes the per-page
// virtualization, the zoom and the page-span scroll possible at all.
import { useEffect, useRef, useState } from 'react'
import { Body1, Button, Caption1, Spinner, makeStyles, tokens } from '@fluentui/react-components'
import {
  ArrowAutofitWidthRegular,
  ArrowRotateClockwiseRegular,
  ArrowRotateCounterclockwiseRegular,
  DocumentRegular,
  OpenRegular,
  ZoomInRegular,
  ZoomOutRegular,
} from '@fluentui/react-icons'
import type { Upload } from '../lib/types'
import type { PdfDoc, PdfView } from './pdfRender'
import { ROTATION_STEP, normalizeRotation } from './pdfRotation'
import { ZOOM_MAX, ZOOM_MIN, type PdfZoom, zoomIn, zoomOut, zoomReadout } from './pdfZoom'
import { ActionDivider } from './ActionDivider'
import { getUploadPdfUrl } from '../lib/data'
import { errorMessage } from '../api/errors'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 4px',
  },
  pages: {
    flex: 1,
    // Below the 280px floor of the box the modal gives the pane, less the bar,
    // so the pane never runs past it (owner, 2026-10-28; 320px until then).
    minHeight: '220px',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'auto',
    // The vertical scrollbar's room is kept whether or not the pages overflow,
    // so the width a page is fitted to (pdfRender.ts) does not shrink when the
    // scrollbar appears and push a horizontal one in under it (D-337).
    scrollbarGutter: 'stable',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px',
    boxSizing: 'border-box',
  },
  // THE TWO ENDS OF THE BAR (owner, 2026-09-14: "left-align the percentage
  // zoom and right-align the Full Screen button"). They rode the same
  // right-hand group from item 11 (2026-08-16) until then, which put the zoom
  // trio in the middle of the bar with nothing on the left of it.
  //
  // Each end anchors itself and the flexible `barGap` between them holds them
  // apart, so neither moves when the other is absent - the zoom trio renders
  // only once the document is `ready`, and Full Screen only where there is an
  // invoice id to open. That is the jump item 11's note was guarding against,
  // solved by the same means on both sides now rather than by `marginLeft:
  // auto` on one.
  zoomGroup: { display: 'flex', alignItems: 'center', gap: '2px' },
  // The rule between the zoom trio and the rotate pair (owner, 2026-10-07:
  // "a thin, vertical line divider between the zoom and rotate buttons"). The
  // same ActionDivider the modal's action bars draw; the slot only adds the
  // breathing room the trio's tight 2px gap does not give it.
  dividerSlot: { display: 'flex', alignItems: 'center', paddingInline: '4px' },
  barGap: { flex: 1, minWidth: '8px' },
  // The no-span case: the pane is showing a whole packet because the record
  // claims no pages of its own. Amber, not red — it is a stub row or an older
  // record, not an error.
  wholeFile: { color: tokens.colorPaletteDarkOrangeForeground1, minWidth: 0 },
  zoomLabel: { minWidth: '42px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  placeholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: tokens.colorNeutralForeground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: '220px',
    padding: '24px',
    textAlign: 'center',
  },
})

// Session cache of signed URLs (uploadId → url + when it expires), so paging
// back and forth through a queue doesn't ask for a new URL every time. Bounded
// FIFO. A URL is dropped a little before it actually expires, so a render that
// starts on a cached URL can still finish.
const URL_CACHE_MAX = 5
const EXPIRY_MARGIN_MS = 30_000

const urlCache = new Map<string, { url: string; expiresAt: number }>()

function cacheUrl(uploadId: string, url: string, expiresAt: string | undefined) {
  if (urlCache.size >= URL_CACHE_MAX) {
    const oldest = urlCache.keys().next().value
    if (oldest) urlCache.delete(oldest)
  }
  const at = expiresAt ? Date.parse(expiresAt) : NaN
  urlCache.set(uploadId, { url, expiresAt: Number.isNaN(at) ? Date.now() + 300_000 : at })
}

function cachedUrl(uploadId: string): string | undefined {
  const hit = urlCache.get(uploadId)
  if (!hit) return undefined
  if (hit.expiresAt - EXPIRY_MARGIN_MS <= Date.now()) {
    urlCache.delete(uploadId)
    return undefined
  }
  return hit.url
}

type FetchState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error'; message: string }

// Capture PDF.js's internal warnings (it logs and carries on when a decoder or a
// font can't be loaded) so a degraded render says why instead of just looking
// wrong.
//
// Reentrant-safe: overlapping calls (e.g. rapid zoom) each register a sink;
// the console is patched once (originals captured on the first call) and
// restored only once every active call has finished — so a nested call can't
// restore a wrapped console over a still-running outer one.
const activeWarningSinks: string[][] = []
let savedConsole: Pick<Console, 'log' | 'warn' | 'error'> | null = null

function collectPdfWarnings<T>(run: () => Promise<T>): Promise<{ result: T; warnings: string[] }> {
  const warnings: string[] = []
  activeWarningSinks.push(warnings)
  if (!savedConsole) {
    savedConsole = { log: console.log, warn: console.warn, error: console.error }
    const orig = savedConsole
    const capture = (fn: (...a: unknown[]) => void) => (...args: unknown[]) => {
      const text = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ')
      if (/warning|wasm|jbig2|jpx|openjpeg|font|fetch/i.test(text))
        for (const sink of activeWarningSinks) if (sink.length < 5) sink.push(text)
      fn(...args)
    }
    console.log = capture(orig.log)
    console.warn = capture(orig.warn)
    console.error = capture(orig.error)
  }
  return run()
    .then((result) => ({ result, warnings }))
    .finally(() => {
      const i = activeWarningSinks.indexOf(warnings)
      if (i >= 0) activeWarningSinks.splice(i, 1)
      if (activeWarningSinks.length === 0 && savedConsole) {
        console.log = savedConsole.log
        console.warn = savedConsole.warn
        console.error = savedConsole.error
        savedConsole = null
      }
    })
}

/**
 * Virtualized page view: pre-sized placeholders per page, canvases rasterized
 * only near the viewport (see `mountPdfView`). Scrolls to `pageStart` on first
 * render; a zoom, a turn or (at fit) a resize re-sizes the pages in place.
 */
function PdfPages({
  url,
  pageStart,
  pageRange,
  zoom,
  rotation,
  onScale,
}: {
  url: string
  pageStart?: number
  /** When set, only these document pages are rendered. */
  pageRange?: { start: number; end: number }
  /** `'fit'` (the default) or a scale, 1 being the page's printed size. */
  zoom: PdfZoom
  /** Degrees clockwise: 0, 90, 180 or 270. */
  rotation: number
  /** The scale in force, whenever it changes — the fitted one included. */
  onScale: (scale: number) => void
}) {
  const s = useStyles()
  const containerRef = useRef<HTMLDivElement>(null)
  // Read as PRIMITIVES, and rebuilt inside the effect below. The parent creates
  // `pageRange` fresh on every render, so depending on the object would remount
  // the whole view on every keystroke elsewhere in the modal.
  const rangeStart = pageRange?.start
  const rangeEnd = pageRange?.end
  // Jump to the invoice's page only on the initial render — a zoom change
  // re-renders and must not yank the reviewer's scroll position around.
  const didInitialScroll = useRef(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  const [diagnostics, setDiagnostics] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  // Parsed document, cached per URL so zoom changes re-render pages without
  // re-fetching or re-parsing the whole PDF.
  const [doc, setDoc] = useState<PdfDoc | null>(null)
  // The mounted virtualized view; reused across zoom changes (never recreated
  // for zoom). Latest zoom in a ref so mounting reads it without a dep.
  const viewRef = useRef<PdfView | null>(null)
  const zoomRef = useRef(zoom)
  const rotationRef = useRef(rotation)
  // The latest callback, read through a ref so a new function from the parent
  // never remounts the view.
  const onScaleRef = useRef(onScale)
  useEffect(() => {
    onScaleRef.current = onScale
  }, [onScale])
  // Warnings accumulate across the per-page renders that fire over time.
  const warningsRef = useRef<Set<string>>(new Set())

  // Load once per URL; destroy the handle when the URL changes or on unmount.
  useEffect(() => {
    let cancelled = false
    let localDoc: PdfDoc | null = null
    setDoc(null)
    setRenderError(null)
    ;(async () => {
      try {
        // Lazy-load the renderer — PDF.js lives in its own chunk (pdfRender.ts).
        const { loadPdfDocument } = await import('./pdfRender')
        const parsed = await loadPdfDocument(url)
        if (cancelled) { void parsed.loadingTask.destroy(); return }
        localDoc = parsed
        setDoc(parsed)
      } catch (e) {
        if (!cancelled) setRenderError((e as Error)?.message ?? 'Failed to render the PDF.')
      }
    })()
    return () => {
      cancelled = true
      if (localDoc) void localDoc.loadingTask.destroy()
    }
  }, [url, retryNonce])

  // Mount the virtualized view when the doc lands. Zoom is applied through the
  // view (below), never by recreating it — so re-rasterizing is scoped to the
  // visible pages.
  useEffect(() => {
    const container = containerRef.current
    if (!container || !doc) return
    let cancelled = false
    warningsRef.current.clear()
    setDiagnostics(null)
    ;(async () => {
      try {
        const { mountPdfView } = await import('./pdfRender')
        const view = await mountPdfView({
          container,
          doc,
          pageStart,
          pageRange: rangeStart === undefined ? undefined : { start: rangeStart, end: rangeEnd ?? rangeStart },
          zoom: zoomRef.current,
          rotation: rotationRef.current,
          onScale: (scale) => {
            if (!cancelled) onScaleRef.current(scale)
          },
          scrollToStart: !didInitialScroll.current,
          wrapWarnings: collectPdfWarnings,
          onWarnings: (w) => {
            for (const line of w) warningsRef.current.add(line)
            const joined = [...warningsRef.current].join(' | ').slice(0, 500)
            setDiagnostics(joined || null)
          },
          onError: (m) => setRenderError(m),
          isCancelled: () => cancelled,
        })
        didInitialScroll.current = true
        if (cancelled) {
          view.destroy()
          return
        }
        viewRef.current = view
        setRenderError(null)
        // A zoom or rotation change during the async mount would have hit a
        // null viewRef.
        view.setZoom(zoomRef.current)
        view.setRotation(rotationRef.current)
      } catch (e) {
        if (!cancelled) setRenderError((e as Error)?.message ?? 'Failed to render the PDF.')
      }
    })()
    return () => {
      cancelled = true
      viewRef.current?.destroy()
      viewRef.current = null
    }
    // The range bounds are in the deps: a record whose span changes under the
    // pane rebuilds the view. The parsed document is NOT re-fetched — the effect
    // above owns that, keyed on the URL — so a range change costs a re-layout,
    // not another download. (It used to be the whole-document toggle that moved
    // these bounds; there is no toggle any more.)
  }, [doc, pageStart, rangeStart, rangeEnd])

  // Zoom re-sizes placeholders and re-renders only the visible pages.
  useEffect(() => {
    zoomRef.current = zoom
    viewRef.current?.setZoom(zoom)
  }, [zoom])

  // Rotation, the same way: through the mounted view, never by remounting it.
  useEffect(() => {
    rotationRef.current = rotation
    viewRef.current?.setRotation(rotation)
  }, [rotation])

  // Container stays mounted through an error (hidden) so the render effect's
  // ref survives — unmounting it stranded every later render at the null check.
  return (
    <>
      {renderError ? (
        <div className={s.placeholder}>
          <DocumentRegular fontSize={36} />
          <Body1>Couldn’t render the document.</Body1>
          <Caption1>{renderError}</Caption1>
          <Button size="small" onClick={() => setRetryNonce((n) => n + 1)}>Retry</Button>
        </div>
      ) : (
        diagnostics && <Caption1>Preview diagnostics: {diagnostics}</Caption1>
      )}
      <div ref={containerRef} className={s.pages} style={renderError ? { display: 'none' } : undefined} />
    </>
  )
}

interface Props {
  upload: Upload | undefined | 'loading'
  pageStart?: number
  pageEnd?: number
  /** The invoice this pane belongs to. When supplied, the header offers a
      "Full Screen" button opening `/?doc=<invoiceId>` in a new tab — the same
      route `main.tsx` mounts standalone, feeding it the same invoice, upload
      and page span (item 2, 2026-08-13). Omit to hide the button (the
      standalone view itself passes no id, so it can't spawn a further tab of
      itself). */
  invoiceId?: string
  /** The turn to open at, in degrees clockwise. The standalone view passes the
      one the modal was showing when Full Screen was clicked, so a page turned
      upright in the modal opens upright in the new tab. */
  initialRotation?: number
}

export function PdfPane({ upload, pageStart, pageEnd, invoiceId, initialRotation }: Props) {
  const s = useStyles()
  const uploadId = upload !== 'loading' ? upload?.id : undefined
  const hasFile = upload !== 'loading' && !!upload?.object_key
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' })
  // FIT TO WIDTH UNTIL THE REVIEWER ZOOMS (owner, 2026-10-09 — D-337: "can the
  // pdf preview be zoomed by default to fit the width of the document, so that
  // previewer does not show the horizontal scroll bar unless the user zooms-in
  // himself?"). `'fit'` follows the pane's width; Zoom In or Zoom Out fixes a
  // scale (pdfZoom.ts); Fit to Width goes back. `fitted` is the scale the view
  // last reported, for the readout and for the first step away from fit.
  const [zoom, setZoom] = useState<PdfZoom>('fit')
  const [fitted, setFitted] = useState<number | null>(null)
  const scaleNow = zoom === 'fit' ? (fitted ?? 1) : zoom
  // Each invoice opens fitted, even when the pane is reused for the next one.
  useEffect(() => {
    setZoom('fit')
  }, [uploadId, pageStart, pageEnd])
  // ROTATION IS A VIEW SETTING, like zoom (owner, 2026-10-01): it turns what
  // this pane draws and nothing else. The stored file is untouched, nothing is
  // saved, and the next invoice opens upright.
  const [rotation, setRotation] = useState(() => normalizeRotation(initialRotation))

  useEffect(() => {
    if (!uploadId || !hasFile) return
    const cached = cachedUrl(uploadId)
    if (cached) {
      setFetchState({ status: 'ready', url: cached })
      return
    }
    let cancelled = false
    setFetchState({ status: 'loading' })
    getUploadPdfUrl(uploadId)
      .then((signed) => {
        if (cancelled) return
        cacheUrl(uploadId, signed.url, signed.expires_at)
        setFetchState({ status: 'ready', url: signed.url })
      })
      .catch((e) => {
        if (!cancelled) setFetchState({ status: 'error', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [uploadId, hasFile])

  if (upload === 'loading') {
    return <div className={s.placeholder}><Spinner size="small" label="Loading document…" /></div>
  }
  if (!upload || !hasFile) {
    return (
      <div className={s.placeholder}>
        <DocumentRegular fontSize={32} />
        <Body1>No source document linked to this invoice.</Body1>
      </div>
    )
  }

  // A span is restrictable only when the record actually claims one, and a span
  // is now the ONLY thing this pane will render: the whole-document toggle is
  // gone (issue 5 — one invoice per modal), so `pageRange` is the span whenever
  // there is one and there is no second path to the rest of the packet.
  //
  // A record with NO span still shows the whole file, because there is nothing
  // else to show it — but it says so. Silence there would be the one case where
  // a reviewer is looking at somebody else's pages believing the opposite, which
  // is precisely what removing the toggle was meant to prevent.
  const span = pageStart ? { start: pageStart, end: pageEnd ?? pageStart } : undefined

  let body
  if (fetchState.status === 'loading') {
    body = <div className={s.placeholder}><Spinner size="small" label="Loading document…" /></div>
  } else if (fetchState.status === 'ready') {
    body = (
      <PdfPages
        url={fetchState.url}
        pageStart={pageStart}
        pageRange={span}
        zoom={zoom}
        rotation={rotation}
        onScale={setFitted}
      />
    )
  } else {
    body = (
      <div className={s.placeholder}>
        <DocumentRegular fontSize={36} />
        <Body1>Couldn’t load the document for inline preview.</Body1>
        <Caption1>{fetchState.message}</Caption1>
      </div>
    )
  }

  return (
    <div className={s.root}>
      <div className={s.bar}>
        {/* The file name is gone (item 8, C3, 2026-08-13) — it named the whole
            packet, and the pane now shows one invoice's pages, so it said the
            wrong thing more often than the right one. The caveat that
            survives it is a real one: when this record claims no page span
            there is nothing else to show but the whole file, and the pane
            says so rather than leaving a reviewer to assume otherwise. A
            record WITH a span renders no Caption1 here at all. */}
        {/* THE ZOOM TRIO IS THE BAR'S LEFT END (owner, 2026-09-14). Rendered
            only once `fetchState` is `ready`: a disabled trio would leave
            permanently dead controls, and a meaningless "100%", beside a
            failed document. Nothing shifts while it is absent - `barGap`
            below takes up the slack and Full Screen stays on the right
            edge. */}
        <div className={s.zoomGroup}>
          {fetchState.status === 'ready' && (
            <>
              <Button
                appearance="subtle"
                size="small"
                icon={<ZoomOutRegular />}
                aria-label="Zoom Out"
                disabled={scaleNow <= ZOOM_MIN}
                onClick={() => setZoom(zoomOut(scaleNow))}
              />
              {/* The scale in force, the fitted one included: "84%" at fit
                  on a narrow pane, "100%" at the page's printed size. */}
              <Caption1 className={s.zoomLabel}>{zoomReadout(zoom, fitted)}</Caption1>
              <Button
                appearance="subtle"
                size="small"
                icon={<ZoomInRegular />}
                aria-label="Zoom In"
                disabled={scaleNow >= ZOOM_MAX}
                onClick={() => setZoom(zoomIn(scaleNow))}
              />
              {/* Back to the default: the widest page fills the pane, and
                  follows it when the pane resizes (D-337). Disabled while
                  already there. */}
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowAutofitWidthRegular />}
                aria-label="Fit to Width"
                title="Fit to Width"
                disabled={zoom === 'fit'}
                onClick={() => setZoom('fit')}
              />
              {/* Rotate, a quarter turn per click (owner, 2026-10-01), for a
                  page scanned sideways or upside down. Beside the zoom trio
                  and shown under the same `ready` rule, for the same reason. */}
              <span className={s.dividerSlot}>
                <ActionDivider />
              </span>
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowRotateCounterclockwiseRegular />}
                aria-label="Rotate Counterclockwise"
                title="Rotate Counterclockwise"
                onClick={() => setRotation((r) => normalizeRotation(r - ROTATION_STEP))}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowRotateClockwiseRegular />}
                aria-label="Rotate Clockwise"
                title="Rotate Clockwise"
                onClick={() => setRotation((r) => normalizeRotation(r + ROTATION_STEP))}
              />
            </>
          )}
        </div>
        {!span && (
          <Caption1 className={s.wholeFile}>
            {upload.file_name ?? 'Document'} · no page span recorded — showing the whole file
          </Caption1>
        )}
        <div className={s.barGap} />
        {/* "Full Screen" (item 2, 2026-08-13): opens `?doc=<invoiceId>` in a
            new tab WITHOUT `rel="noopener"` — `window.open` here keeps the
            opener relationship by default, which is what lets the new tab's
            sessionStorage start as a copy of this one's (MSAL's
            `cacheLocation: 'sessionStorage'`, api/auth.ts), so it doesn't
            land signed out. `main.tsx` mounts this SAME component there,
            fed the same invoice + page span — never the signed upload URL
            itself, which stays fetched fresh in whichever tab needs it.
            `window.location.pathname` rather than a hard-coded `/`: the SPA
            is not guaranteed to be served at the site root.

            "Full Screen", both words capitalized since 2026-09-14 (owner):
            every other labelled control in this app title-cases its words. */}
        {invoiceId && (
          <Button
            appearance="subtle"
            size="small"
            icon={<OpenRegular />}
            onClick={() =>
              window.open(
                `${window.location.pathname}?doc=${encodeURIComponent(invoiceId)}${rotation ? `&rotate=${rotation}` : ''}`,
                '_blank',
              )
            }
          >
            Full Screen
          </Button>
        )}
      </div>
      {body}
    </div>
  )
}
